import { Snackbar } from '@mui/base';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, ImageListItem,
  ImageListItemBar, InputLabel, Stack
} from '@mui/material';
import { addDoc, collection } from 'firebase/firestore';
import { getStorage, ref, uploadBytes } from "firebase/storage";
import React, { useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserTags } from '../../hooks/tags';
import auth from '../../lib/auth';
import { db } from '../../lib/firebase';
import ConfirmationModal from '../shared/ConfirmationModal';
import NewTagModal from './NewTagModal';


const OutfitForm = ({ outfitImageFile, onOutfitSaved, remainingPicturesCount }) => {
  const [selectedTags, setSelectedTags] = useState([]); // outfit tags
  const [outfitError, setOutfitError] = useState(null)
  const [favorite, setFavorite] = useState(false);
  const { t } = useTranslation();
  const [momentCreatedMessage, setMomentCreatedMessage] = useState(null);
  const [user] = useAuthState(auth)
  const [userTags] = useUserTags(user?.uid);
  const navigate = useNavigate();

  const storage = getStorage();

  const clearFormFields = () => {
    setSelectedTags([])
  }

  const handleFavoriteClick = () => {
    setFavorite(!favorite);
  };


  const handleSubmit = (e) => {
    e.preventDefault();

    const imageUuid = `${user.uid}-${crypto.randomUUID()}`;
    const storageRef = ref(storage, `outfits/${imageUuid}`);

    uploadBytes(storageRef, outfitImageFile, { cacheControl: 'public, max-age=604800' }).then((snapshot) => {

    }).catch((error) => {
      setOutfitError(t('newOutfit.error'))
    });

    handleCreateOutfit(imageUuid)
  };


  const handleCreateOutfit = async (imageUuid) => {
    try {
      await addDoc(collection(db, 'users', user.uid, 'outfits'), {
        coverPhoto: imageUuid,
        favorite: favorite,
        moments: selectedTags
      });
      setOutfitError(null)
      clearFormFields()
      onOutfitSaved();
      if (remainingPicturesCount === 0) {
        navigate("/");
      }
    } catch (error) {
      setOutfitError(t('newOutfit.error'))
    }
  }

  const handleCloseSnackbarMoment = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setMomentCreatedMessage(null);
  };

  const handleDiscart = () => {
    onOutfitSaved();
    if (remainingPicturesCount === 0) {
      navigate("/");
    }
  }

  const handleClickChip = (tagId) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter(tid => tid !== tagId))
    } else {
      setSelectedTags([...selectedTags, tagId])
    }
  };


  if (outfitImageFile == null) return <><CircularProgress /></>

  if (userTags == null) {
    return <><CircularProgress /></>
  }

  return (
    <Box>
      <Box>
        <ConfirmationModal />
        <form onSubmit={handleSubmit}>
          <ImageListItem sx={{ marginLeft: '130px', marginRight: '130px' }}>
            <img src={URL.createObjectURL(outfitImageFile)} alt={`Preview ${outfitImageFile.name}`} height={'100%'} width={'100%'} />
            <ImageListItemBar
              sx={{
                background:
                  'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, ' +
                  'rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
              }}
              position="top"
              actionIcon={
                <IconButton
                  sx={{ color: 'white' }}
                  onClick={handleFavoriteClick}
                >
                  {favorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                </IconButton>
              }
              actionPosition="right"
            />
          </ImageListItem>
          <InputLabel htmlFor="moments-input" sx={{ fontWeight: 'bold' }}>{t('newOutfit.moments')}*</InputLabel>
          <Box>
            <Stack direction="row" spacing={1} sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
              {userTags.map((tag) => (
                <Chip
                  label={tag.name} onClick={() => handleClickChip(tag.id)} sx={{ mb: 1 }}
                  color={selectedTags.includes(tag.id) ? "primary" : "default"}
                  key={tag.id}
                />
              ))}
            </Stack>
            <NewTagModal userId={user.uid} setMomentCreatedMessage={setMomentCreatedMessage} onTagCreated={(tagId) => { handleClickChip(tagId) }} />
          </Box>
          <Stack spacing={2} sx={{ width: '100%', marginTop: 1 }}>
            <Snackbar open={momentCreatedMessage != null} autoHideDuration={6000} onClose={handleCloseSnackbarMoment} sx={{ width: '100%' }}>
              <Alert onClose={handleCloseSnackbarMoment} severity="success">
                {t('newOutfit.momentCreated')} {momentCreatedMessage}
              </Alert>
            </Snackbar>
          </Stack>
        </form>
      </Box>
      <Box sx={{ width: '100%', padding: '0.5rem', textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
          <Button onClick={handleDiscart} variant="contained" color="primary" sx={{ marginBottom: '1rem' }}>
            <CloseIcon />
          </Button>
          <Button onClick={handleSubmit} type="submit" variant="contained" color="primary" sx={{ marginBottom: '1rem' }}>
            <DoneIcon />
          </Button>
        </Box>
        <Alert severity="info" sx={{ marginBottom: '1rem' }}>{t('newOutfit.remaindPhotos')} {remainingPicturesCount}</Alert>
        {outfitError != null &&
          <Alert severity="error" sx={{ marginBottom: '1rem' }}>{outfitError}</Alert>
        }
      </Box>
    </Box>
  );
};

export default OutfitForm;